/**
 * Indicator showing which MCP tool modules are available to the AI
 * and highlighting modules that are actively being used.
 *
 * Shows module icons with short names, access level badges (R / RW),
 * and status animations. Tooltips provide full details.
 *
 * Optionally shows a "Configure" button that opens the McpConfigPanel.
 */
import React, { useMemo } from 'react';
import { Loader2, Check, X, Server } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { McpConfigPanel } from './McpConfigPanel';
import { SystemPromptViewer } from './SystemPromptViewer';
import { getModuleById } from '../../config/modules.config';
import {
  MCP_TO_MODULE_ID,
  normalizeLegacyMcpModuleName,
  normalizeMcpModuleNames,
} from './mcpModuleMapping';
import type { ToolEvent, McpConfig } from './types';

/** Short display names for modules in the indicator bar */
const MODULE_SHORT_NAMES: Record<string, string> = {
  tasks: 'Tasks',
  sales: 'Sales',
  recruiting: 'Recruiting',
  accounting: 'Accounting',
  people: 'People',
  products: 'Products',
  email: 'Email',
  file_management: 'Files',
  support: 'Support',
  contacts: 'Contacts',
  website_hosting: 'Websites',
  ai_agents: 'AI Agents',
  user_management: 'Users',
  messaging: 'Messages',
};

type ModuleStatus = 'available' | 'running' | 'success' | 'error';

interface ToolModulesIndicatorProps {
  /** Modules enabled for this chat session */
  enabledModules: string[];
  /** Tool events from current streaming response */
  toolEvents?: ToolEvent[];
  /** Currently active tool name */
  activeToolName?: string | null;
  /** Whether to show the MCP configure button */
  showConfigButton?: boolean;
  /** Current MCP configuration (for config panel) */
  mcpConfig?: McpConfig;
  /** Called when MCP configuration changes (for config panel) */
  onMcpConfigChange?: (config: McpConfig) => void;
  /** Session ID for the system prompt viewer */
  sessionId?: string;
}

export const ToolModulesIndicator: React.FC<ToolModulesIndicatorProps> = ({
  enabledModules,
  toolEvents = [],
  activeToolName,
  showConfigButton = false,
  mcpConfig,
  onMcpConfigChange,
  sessionId,
}) => {
  const normalizedModules = useMemo(
    () => normalizeMcpModuleNames(enabledModules),
    [enabledModules],
  );

  // Build a map of external__<uuid> → server name from React Query cache
  const queryClient = useQueryClient();
  const externalServerNames = useMemo(() => {
    const map = new Map<string, string>();
    const hasExternal = normalizedModules.some(m => m.startsWith('external__'));
    if (!hasExternal) return map;

    // Read from the same cache key McpConfigPanel uses
    const cached = queryClient.getQueryData<Array<{ id: string; name: string }>>(
      ['mcp-servers', { includeInactive: false }]
    );
    if (cached) {
      for (const server of cached) {
        map.set(`external__${server.id}`, server.name);
      }
    }
    return map;
  }, [normalizedModules, queryClient]);

  if (enabledModules.length === 0 && !showConfigButton && !sessionId) return null;

  // Compute per-module status from tool events
  const moduleStatuses = new Map<string, ModuleStatus>();
  for (const module of normalizedModules) {
    moduleStatuses.set(module, 'available');
  }

  for (const event of toolEvents) {
    const current = moduleStatuses.get(event.module);
    if (!current) continue;

    if (event.status === 'running') {
      moduleStatuses.set(event.module, 'running');
    } else if (event.status === 'error') {
      moduleStatuses.set(event.module, 'error');
    } else if (event.status === 'success' && current !== 'running' && current !== 'error') {
      moduleStatuses.set(event.module, 'success');
    }
  }

  // Check if any running event is in this module based on activeToolName
  if (activeToolName) {
    for (const event of toolEvents) {
      if (event.toolName === activeToolName && event.status === 'running') {
        moduleStatuses.set(event.module, 'running');
      }
    }
  }

  return (
    <div className="flex items-center gap-0.5 min-w-0">
      {/* Config button — always visible */}
      {showConfigButton && mcpConfig && onMcpConfigChange && (
        <div className="shrink-0">
          <McpConfigPanel
            config={mcpConfig}
            onChange={onMcpConfigChange}
          />
        </div>
      )}
      {/* Module icons — fill available space, hidden overflow */}
      {normalizedModules.length > 0 && (
        <div className="flex items-center min-w-0 overflow-hidden">
          {normalizedModules.map(module => {
            const status = moduleStatuses.get(module) || 'available';
            const legacyModule = enabledModules.find(
              candidate => normalizeLegacyMcpModuleName(candidate) === module,
            );
            const accessLevel =
              mcpConfig?.moduleAccess?.[module] ||
              (legacyModule ? mcpConfig?.moduleAccess?.[legacyModule] : undefined);
            const externalName = externalServerNames.get(module);
            return (
              <ModuleChip
                key={module}
                moduleName={module}
                status={status}
                accessLevel={accessLevel}
                externalServerName={externalName}
              />
            );
          })}
        </div>
      )}
      {/* Debug inspector — always visible */}
      {sessionId && (
        <div className="shrink-0">
          <SystemPromptViewer sessionId={sessionId} />
        </div>
      )}
    </div>
  );
};

/** Module chip: icon + short name + access level indicator */
const ModuleChip: React.FC<{
  moduleName: string;
  status: ModuleStatus;
  accessLevel?: 'read' | 'readwrite' | 'approve';
  externalServerName?: string;
}> = ({ moduleName, status, accessLevel, externalServerName }) => {
  const isExternal = moduleName.startsWith('external__');
  const moduleId = isExternal ? moduleName : (MCP_TO_MODULE_ID[moduleName] || moduleName);
  const moduleConfig = isExternal ? null : getModuleById(moduleId);
  const icon = isExternal ? <Server className="h-3 w-3" /> : moduleConfig?.icon;
  const shortName = isExternal
    ? (externalServerName || 'MCP Server')
    : (MODULE_SHORT_NAMES[moduleName] || moduleName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
  const statusLabel = {
    available: 'Available',
    running: 'Running...',
    success: 'Used',
    error: 'Error',
  }[status];

  const accessLabel = accessLevel === 'read' ? 'Read only' : accessLevel === 'approve' ? 'Read + Write (approval required)' : accessLevel === 'readwrite' ? 'Read + Write' : '';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'inline-flex items-center justify-center rounded h-6 w-6 shrink-0 transition-colors duration-300',
            status === 'available' && 'text-muted-foreground hover:bg-muted/30',
            status === 'running' && 'text-info bg-info/10',
            status === 'success' && 'text-success bg-success/10',
            status === 'error' && 'text-destructive bg-destructive/10',
          )}
        >
          {status === 'running' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : status === 'success' ? (
            <Check className="h-3.5 w-3.5" />
          ) : status === 'error' ? (
            <X className="h-3.5 w-3.5" />
          ) : icon ? (
            <span className="h-3.5 w-3.5 shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
          ) : null}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">
        <div className="text-xs">
          <p className="font-medium">{shortName}</p>
          {accessLabel && <p className="text-muted-foreground">{accessLabel}</p>}
          <p className="text-muted-foreground">{statusLabel}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
